import React, { useState, useMemo } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { CounterpartyModel, UpdateAllocationGroupRequest, Unit } from '../../../../Generated/Commodity-Management-Api';
import { useSnackbar } from 'notistack';
import { StoneXTextField, StoneXAutocomplete } from '../../../StoneXMui';
import { getFiscalYearMonths, parseFiscalYearLabel } from '../../../../Helpers';

interface AddSupplierModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    locationId: number;
    commodityId: number;
    unit: Unit;
    fiscalYear: string;
    fiscalMonthOffset: number;
    existingCounterpartyIds: number[];
    allCounterparties: CounterpartyModel[];
    units: Unit[];
}

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({
    open,
    onClose,
    onSuccess,
    locationId,
    commodityId,
    unit,
    fiscalYear,
    fiscalMonthOffset,
    existingCounterpartyIds,
    allCounterparties,
    units
}) => {
    const [selectedCounterparty, setSelectedCounterparty] = useState<CounterpartyModel | null>(null);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<string>('');
    const [selectedUnit, setUnit] = useState<string>(unit);
    const [isSaving, setIsSaving] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const availableCounterparties = useMemo(() => {
        return allCounterparties.filter(c => !existingCounterpartyIds.includes(c.counterpartyId!));
    }, [allCounterparties, existingCounterpartyIds]);

    const generateMonthlyVolume = () => {
        const monthlyVolume: Record<string, number> = {};
        const startYear = parseFiscalYearLabel(fiscalYear);
        if (startYear == null) return JSON.stringify(monthlyVolume);

        // The fiscal year starts one month past the offset, matching the range
        // the dashboard queries budgets and positions over. Using the raw
        // offset here shifted every supplier a month earlier than its budgets.
        for (const { year, month } of getFiscalYearMonths(startYear, fiscalMonthOffset)) {
            monthlyVolume[`${year}-${String(month).padStart(2, '0')}`] = 0;
        }
        return JSON.stringify(monthlyVolume);
    };

    const handleAdd = async () => {
        if (!selectedCounterparty) {
            enqueueSnackbar('Please select a supplier.', { variant: 'warning' });
            return;
        }
        setIsSaving(true);
        try {
            const newAllocationGroup: UpdateAllocationGroupRequest = {
                allocationGroupId: 0,
                allocationName: selectedPurchaseOrder,
                commodityId: commodityId,
                locationId: locationId,
                unit:unit,
                counterpartyId: Number(selectedCounterparty.counterpartyId),
                monthlyVolume: generateMonthlyVolume(),
            };

            await CommodityManagementApi.addAllocationGroup(null, newAllocationGroup);

            enqueueSnackbar('Supplier added successfully.', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Failed to add supplier", error);
            enqueueSnackbar('An error occurred while adding the supplier.', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Supplier</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <StoneXAutocomplete
                        options={availableCounterparties ?? []}
                        getOptionLabel={(q) => q.counterpartyName!}
                        value={selectedCounterparty}
                        onChange={(e: any, counterparty: CounterpartyModel | null) => setSelectedCounterparty(counterparty)}
                        isOptionEqualToValue={(a, b) => a.counterpartyId == b.counterpartyId}
                        label="Counterparty"
                        showRequired={true}
                        disabled={isSaving}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <StoneXTextField
                        type="text"
                        label="Deal Ticket"
                        value={selectedPurchaseOrder}
                        onChange={(event) => setSelectedPurchaseOrder(event.target.value)}
                        disabled={isSaving}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <StoneXAutocomplete
                        options={units ?? []}
                        getOptionLabel={(q) => q!}
                        value={unit}
                        onChange={(e: any, unit: Unit | null) => setUnit(unit!.toString())}
                        isOptionEqualToValue={(a, b) => a == b}
                        label="Unit"
                        showRequired={true}
                        disabled={isSaving}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleAdd} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSupplierModal;